import type { DoseDefinition, SkipDoseCompletionRuleV1, VaccinationRecord } from '../types'

// 互斥劑次配對：確認一方時自動 skip 另一方
const EXCLUSIVE_DOSE_PAIRS: [string, string][] = [
  ['pcv13-1', 'pcv15-d1'],   // 2個月
  ['pcv13-1', 'pcv20-d1'],   // 2個月
  ['pcv15-d1', 'pcv20-d1'],  // 2個月
  ['pcv13-2', 'pcv15-d2'],   // 4個月
  ['pcv13-2', 'pcv20-d2'],   // 4個月
  ['pcv15-d2', 'pcv20-d2'],  // 4個月
  ['pcv13-s3', 'pcv15-d3'],  // 6個月
  ['pcv13-s3', 'pcv20-d4'],  // 6個月
  ['pcv15-d3', 'pcv20-d4'],  // 6個月
  ['pcv13-3', 'pcv15-d4'],   // 12個月
  ['pcv13-3', 'pcv20-d3'],   // 12個月
  ['pcv15-d4', 'pcv20-d3'],  // 12個月
  ['pcv15-1', 'pcv20-1'],    // 15個月（補充劑）
  ['6in1-1', '5in1-3'],      // 6個月
  ['6in1-1', 'hepB-3'],      // 6個月
  ['6in1-2', '5in1-4'],      // 18個月
]


/** 與確認接種共用的宣告式規則及舊版配對；不加入新的接種規則。 */
type ResolvedSkipRule = SkipDoseCompletionRuleV1 & { legacy?: boolean }

export function getExclusiveSkipRules(doseId: string, dose?: DoseDefinition): ResolvedSkipRule[] {
  const rules = dose?.completionRules?.filter(rule => rule.version === 1 && rule.type === 'skipDoses') ?? []
  if (rules.length) return rules
  const targetDoseIds = EXCLUSIVE_DOSE_PAIRS
    .filter(pair => pair.includes(doseId))
    .map(pair => pair[0] === doseId ? pair[1] : pair[0])
  return targetDoseIds.length
    ? [{ version: 1, type: 'skipDoses', targetDoseIds, onlyIfStatus: ['planned', 'overdue'], legacy: true }]
    : []
}

/**
 * 判斷目前規則是否成立，不依賴舊資料是否曾記下略過來源。
 * 是否允許新增來源紀錄，另外由 matchesSkipRule 判斷。
 */
function appliesSkipRule(target: VaccinationRecord, rule: ResolvedSkipRule): boolean {
  return rule.targetDoseIds.includes(target.doseId) &&
    (rule.onlyIfStatus.includes(target.status as 'planned' | 'overdue') ||
      (target.status === 'skipped' && rule.onlyIfStatus.length > 0) ||
      (rule.legacy === true && target.status === 'expired'))
}

/** 新增略過來源時才檢查來源是否可追蹤；不把此限制套在規則是否仍成立的判斷上。 */
export function matchesSkipRule(target: VaccinationRecord, rule: ResolvedSkipRule): boolean {
  if (target.status === 'skipped' && !target.manualSkip && !target.skippedByDoseIds?.length) return false
  return appliesSkipRule(target, rule)
}

/** 已有的 BCG、輪狀、流感特殊完成流程可能影響的項目（輪狀取決於劑型選項）。 */
export function hasSpecialCompletionImpact(source: VaccinationRecord, target: VaccinationRecord): boolean {
  if (source.vaccineId !== target.vaccineId) return false
  if (source.vaccineId === 'bcg') return source.doseIndex === 1 && target.doseIndex === 2
  if (source.vaccineId === 'rota') return source.doseIndex === 1 && target.doseIndex === 3
  return source.vaccineId === 'flu' && source.doseIndex % 2 === 1 &&
    target.doseIndex % 2 === 0 && target.doseIndex !== source.doseIndex + 1
}

/** 復原也重查完成紀錄，修復舊版本手動隱藏時未記下的互斥來源。 */
export function getActiveSkipSources(
  target: VaccinationRecord,
  records: VaccinationRecord[],
  getDose: (id: string) => DoseDefinition | undefined,
): string[] {
  const siblings = records.filter(record => record.babyId === target.babyId)
  // 未載到來源時保守保留；來源明確已取消確認時才移除。
  const sources = new Set((target.skippedByDoseIds ?? []).filter(id => {
    const source = siblings.find(record => record.doseId === id)
    return !source || source.status === 'completed'
  }))
  for (const source of siblings) {
    if (source.id === target.id || source.status !== 'completed') continue
    const exclusive = getExclusiveSkipRules(source.doseId, getDose(source.doseId))
      .some(rule => appliesSkipRule(target, rule))
    // 輪狀的 2/3 劑型僅能由既有 provenance 判斷，不能憑第1劑已完成推測。
    const special = source.vaccineId !== 'rota' && hasSpecialCompletionImpact(source, target)
    if (exclusive || special) sources.add(source.doseId)
  }
  return [...sources]
}
